import DOMPurify from 'dompurify';
import { logger } from '@/utils/logger';

/**
 * Normalizes HTML content by fixing common WordPress formatting issues.
 * - Converts double <br><br> to proper paragraph breaks
 * - Removes empty paragraphs
 */
export function normalizeHtmlContent(html: string): string {
  if (!html) return '';
  
  return html
    // Convert double <br> variants to paragraph breaks (handles <br>, <br/>, <br />, and whitespace between)
    .replace(/<br\s*\/?>\s*<br\s*\/?>/gi, '</p><p>')
    // Remove empty paragraphs
    .replace(/<p>\s*<\/p>/gi, '')
    .replace(/<p>&nbsp;<\/p>/gi, '')
    .trim();
}

// Safe SVG tags for icon rendering
const SVG_TAGS = ['svg', 'path', 'g', 'circle', 'rect', 'line', 'polyline', 'polygon'];

// Safe SVG attributes
const SVG_ATTRS = [
  'viewBox', 'fill', 'stroke', 'stroke-width', 'stroke-linecap', 'stroke-linejoin',
  'd', 'xmlns', 'aria-hidden', 'role', 'cx', 'cy', 'r', 'x', 'y', 'x1', 'y1', 'x2', 'y2',
  'points', 'transform', 'opacity', 'fill-rule', 'clip-rule',
];

// WordPress-specific class patterns to strip
const WP_CLASS_PATTERNS = [
  /\bwp-[a-zA-Z0-9_-]+/g,
  /\bhas-[a-zA-Z0-9_-]+/g,
  /\balignleft\b/g,
  /\balignright\b/g,
  /\baligncenter\b/g,
  /\balignwide\b/g,
  /\balignnone\b/g,
  /\bwp-block-[a-zA-Z0-9_-]+/g,
  /\bis-style-[a-zA-Z0-9_-]+/g,
];

/**
 * Enhanced publication content sanitizer for WordPress migration.
 * Strips inline styles, normalizes structure, preserves semantic HTML.
 * 
 * @param html - Raw HTML content from WordPress
 * @returns Sanitized HTML ready for consistent rendering
 */
export function sanitizePublicationHtml(html: string | null | undefined): string {
  if (!html) return '';
  
  // 0. Pre-normalize: strip <br> tags adjacent to table structural elements
  // WordPress exports interleave <br> between every table tag, breaking regex matching
   // 0-pre. Normalize line endings: \r\n -> \n, lone \r -> \n
   // 80,000+ Portuguese WordPress publications use \r (CR) instead of \n (LF)
   // All subsequent newline rules assume \n, so this must run first
   let processed = html
    .replace(/\r\\n/g, '\n')   // CR + literal \n -> single real LF
    .replace(/\r\n/g, '\n')    // Real CRLF -> LF
    .replace(/\r/g, '\n')      // Lone CR -> LF

    // Convert WordPress [caption] shortcodes to <figure>/<figcaption>
    // Format: [caption id="..." align="..." width="..." class="..."]<img .../>Caption text[/caption]
    .replace(
      /\[caption[^\]]*\]([\s\S]*?)\[\/caption\]/gi,
      (_match, inner: string) => {
        // Extract the <img> tag(s) and the remaining text as caption
        const imgMatch = inner.match(/(<img[^>]*\/?>)/i);
        const img = imgMatch ? imgMatch[1] : '';
        const captionText = inner
          .replace(/<img[^>]*\/?>/gi, '')  // Remove img tags
          .replace(/<br\s*\/?>/gi, '')     // Remove stray <br>
          .trim();
        const figcaption = captionText ? `<figcaption>${captionText}</figcaption>` : '';
        return `<figure>${img}${figcaption}</figure>`;
      }
    )

    .replace(/<br\s*\/?>\s*(<\/?(table|tbody|thead|tr|td|th)[^>]*>)/gi, '$1')
    .replace(/(<\/?(table|tbody|thead|tr|td|th)[^>]*>)\s*<br\s*\/?>/gi, '$1');

  // 0b. Strip WordPress artifact tables iteratively (nested tables need multiple passes)
  let prevLength: number;
  do {
    prevLength = processed.length;
    processed = processed.replace(/<table[^>]*cell(?:spacing|padding)[^>]*>[\s\S]*?<\/table>/gi, (match) => {
      const textContent = match.replace(/<[^>]*>/g, '').replace(/\s+/g, ' ').trim();
      return textContent === '0' || textContent === '' ? '' : match;
    });
  } while (processed.length !== prevLength);

  processed = processed

    // 0c. Strip literal \n sequences (AI translation artifacts)
    // Must run BEFORE actual newline normalization
    .replace(/\\n\\n/g, '</p><p>')   // Double literal \n -> paragraph break
    .replace(/\\n/g, '<br>')          // Single literal \n -> line break
    .replace(/\\t/g, '')              // Strip literal \t (WordPress tab artifacts)

    // 0d. Strip data-start/data-end attributes (WordPress/editor artifacts)
    .replace(/\s*data-(?:start|end)="[^"]*"/gi, '')

    // 0e. Collapse whitespace between list tags so double-newline rule doesn't break lists
    .replace(/<\/li>\s+<li/gi, '</li><li')
    .replace(/<ul[^>]*>\s+<li/gi, (m) => m.replace(/>\s+<li/, '><li'))
    .replace(/<\/li>\s+<\/ul>/gi, '</li></ul>')
    .replace(/<ol[^>]*>\s+<li/gi, (m) => m.replace(/>\s+<li/, '><li'))
    .replace(/<\/li>\s+<\/ol>/gi, '</li></ol>')

    // 0f. Strip <p> tags inside <li> (WordPress wraps list text in redundant paragraphs)
    .replace(/<li([^>]*)>\s*<p([^>]*)>([\s\S]*?)<\/p>\s*<\/li>/gi, '<li$1>$3</li>')

    // 1. Normalize excessive newlines first (3+ becomes 2)
    .replace(/\n{3,}/g, '\n\n')
    
    // 2. Convert <br><br> to proper paragraphs (WordPress content)
    .replace(/<br\s*\/?>\s*<br\s*\/?>/gi, '</p><p>')
    
    // 2b. Collapse whitespace between block-level tags (WordPress formatting)
    // Prevents the double-newline rule below from injecting ghost paragraphs
    .replace(
      /(<\/(?:p|h[1-6]|ul|ol|li|blockquote|figure|table|pre|div|hr)>)\s+(<(?:p|h[1-6]|ul|ol|li|blockquote|figure|table|pre|div|hr)[\s>])/gi,
      '$1$2'
    )

    // 2c. Strip <br> tags between block-level elements (they add unwanted spacing)
    // Lone \r characters get converted to <br> by DOMParser; this catches those artifacts
    .replace(
      /(<\/(?:p|h[1-6]|ul|ol|li|blockquote|figure|table|pre|div)>)\s*<br\s*\/?>\s*(<(?:p|h[1-6]|ul|ol|li|blockquote|figure|table|pre|div|hr)[\s>])/gi,
      '$1$2'
    )

    // 3. Convert double newlines to paragraphs (AI translation output)
    .replace(/\n\s*\n/g, '</p><p>')
    
    // NOTE: Single \n preserved intentionally - often within HTML structure
    
    // 4. Remove empty paragraphs and &nbsp; placeholders
    .replace(/<p>\s*<\/p>/gi, '')
    .replace(/<p>&nbsp;<\/p>/gi, '')
    .replace(/<p>\s*&nbsp;\s*<\/p>/gi, '')
    // Strip empty divs (WordPress layout wrappers)
    .replace(/<div>\s*<\/div>/gi, '')
    .replace(/<div>&nbsp;<\/div>/gi, '')
    // Strip empty spans
    .replace(/<span>\s*<\/span>/gi, '')
    .replace(/<span>&nbsp;<\/span>/gi, '')
    // Collapse runs of &nbsp; (WordPress spacers) to single space
    .replace(/(&nbsp;\s*){3,}/gi, ' ')
    // Collapse 3+ consecutive <br> into one
    .replace(/(<br\s*\/?>[\s]*){3,}/gi, '<br>')
    
    // 5. Strip ALL inline styles (WordPress cruft)
    .replace(/\s*style="[^"]*"/gi, '')
    .replace(/\s*style='[^']*'/gi, '')
    
    // 6. Clean up WordPress figure/figcaption (remove inline attributes)
    .replace(/<figure[^>]*>/gi, '<figure>')
    .replace(/<figcaption[^>]*>/gi, '<figcaption>')
    
    // 7. Trim whitespace
    .trim();

  // 7. Wrap leading text in <p> if content doesn't start with a block element
  if (!/^\s*<(p|h[1-6]|ul|ol|blockquote|figure|table|pre|hr|div)/i.test(processed)) {
    processed = '<p>' + processed;
  }

  // 7b. Ensure content ends with a closing block tag
  if (processed.length > 0 && !/<\/(p|h[1-6]|ul|ol|blockquote|figure|table|pre|div)>\s*$/i.test(processed)) {
    processed = processed + '</p>';
  }

  // Use a hook to strip WordPress classes during sanitization
  DOMPurify.addHook('uponSanitizeAttribute', (node, data) => {
    if (data.attrName === 'class' && data.attrValue) {
      let classes = data.attrValue;
      for (const pattern of WP_CLASS_PATTERNS) {
        classes = classes.replace(pattern, '');
      }
      // Clean up extra spaces
      data.attrValue = classes.replace(/\s+/g, ' ').trim();
    }
  });

  let result = DOMPurify.sanitize(processed, {
    USE_PROFILES: { html: true },
    ALLOWED_TAGS: [
      'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
      'p', 'br', 'hr',
      'ul', 'ol', 'li',
      'strong', 'b', 'em', 'i', 'u', 's', 'strike', 'mark',
      'a', 'img',
      'blockquote', 'pre', 'code',
      'table', 'thead', 'tbody', 'tr', 'th', 'td',
      'figure', 'figcaption',
      'sup', 'sub',
      'div', 'span',
    ],
    ALLOWED_ATTR: [
      'href', 'src', 'alt', 'title',
      'target', 'rel', 'width', 'height',
      'id', // For anchor links
      'class', // Keep class for any semantic styling
    ],
    ADD_ATTR: ['target', 'rel'],
  });

  // Remove the hook after use to prevent side effects
  DOMPurify.removeHook('uponSanitizeAttribute');

  // 8. Post-process: wrap orphan text nodes after block elements using DOM
  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(`<div>${result}</div>`, 'text/html');
    const container = doc.body.firstElementChild;

    if (container) {
      // Safety net: remove empty table elements that survived regex
      const emptyTableEls = container.querySelectorAll('table, tbody, thead, tr, td, th');
      emptyTableEls.forEach((el) => {
        if (!el.textContent?.trim()) {
          el.remove();
        }
      });

      // Remove <p> tags containing only whitespace or <br>
      const paragraphs = container.querySelectorAll('p');
      paragraphs.forEach((p) => {
        const inner = p.innerHTML.replace(/<br\s*\/?>/gi, '').trim();
        if (!inner || inner === '&nbsp;') {
          p.remove();
        }
      });

      // Unwrap <p> tags inside <li> (WordPress wraps list text in redundant paragraphs)
      // DOM-based approach is more robust than regex for nested HTML
      const liParagraphs = container.querySelectorAll('li > p');
      liParagraphs.forEach((p) => {
        while (p.firstChild) {
          p.parentNode!.insertBefore(p.firstChild, p);
        }
        p.remove();
      });

      // Strip leading/trailing <br> inside <li> (artifacts from literal \n conversion)
      const allLis = container.querySelectorAll('li');
      allLis.forEach((li) => {
        while (li.firstChild && li.firstChild.nodeName === 'BR') {
          li.removeChild(li.firstChild);
        }
        while (li.firstChild?.nodeType === Node.TEXT_NODE && !li.firstChild.textContent?.trim() && li.firstChild.nextSibling?.nodeName === 'BR') {
          li.removeChild(li.firstChild.nextSibling!);
          li.removeChild(li.firstChild);
        }
        while (li.lastChild && li.lastChild.nodeName === 'BR') {
          li.removeChild(li.lastChild);
        }
      });

      // Collapse consecutive <br> in the final output
      // (handled via innerHTML replacement below)

      // Find all direct text node children that have content
      const childNodes = Array.from(container.childNodes);
      
      for (let i = 0; i < childNodes.length; i++) {
        const node = childNodes[i];
        
        if (node.nodeType === Node.TEXT_NODE) {
          const text = node.textContent?.trim();
          if (text) {
            const p = doc.createElement('p');
            p.textContent = node.textContent;
            node.parentNode?.replaceChild(p, node);
          } else {
            node.parentNode?.removeChild(node);
          }
        }
      }

      result = container.innerHTML;
      // Final pass: collapse 3+ consecutive <br> to single
      result = result.replace(/(<br\s*\/?>[\s]*){3,}/gi, '<br>');
    }
  } catch (e) {
    // If DOM parsing fails, return the regex-processed result
    logger.warn('DOM post-processing failed, using regex result:', e);
  }

  return result;
}

/**
 * Sanitizes HTML content to prevent XSS attacks.
 * Use this for all dangerouslySetInnerHTML content from the database.
 * @param html - The HTML content to sanitize
 * @param options - Optional settings
 * @param options.normalize - Whether to normalize double line breaks (default: true)
 * @param options.allowSvg - Whether to allow safe SVG elements for icons (default: false)
 */
export function sanitizeHtml(
  html: string | null | undefined,
  options?: { normalize?: boolean; allowSvg?: boolean }
): string {
  if (!html) return '';
  
  // Normalize double line breaks by default
  let processed = html;
  if (options?.normalize !== false) {
    processed = normalizeHtmlContent(processed);
  }

  const baseTags = [
    'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
    'p', 'br', 'hr',
    'ul', 'ol', 'li',
    'strong', 'b', 'em', 'i', 'u', 's', 'strike',
    'a', 'img',
    'blockquote', 'pre', 'code',
    'table', 'thead', 'tbody', 'tr', 'th', 'td',
    'div', 'span',
    'figure', 'figcaption',
  ];

  const baseAttrs = [
    'href', 'src', 'alt', 'title', 'class', 'id',
    'target', 'rel', 'width', 'height', 'style',
    'data-*',
  ];

  const addAttrs = options?.allowSvg 
    ? [...SVG_ATTRS, 'target', 'rel'] 
    : ['target', 'rel'];

  // When allowSvg is true, add SVG tags to the default profile
  // Using ADD_TAGS works better with USE_PROFILES for SVG support
  if (options?.allowSvg) {
    return DOMPurify.sanitize(processed, {
      USE_PROFILES: { html: true, svg: true },
      ALLOWED_TAGS: [...baseTags, ...SVG_TAGS],
      ALLOWED_ATTR: [...baseAttrs, ...SVG_ATTRS],
      ADD_ATTR: addAttrs,
    });
  }

  return DOMPurify.sanitize(processed, {
    USE_PROFILES: { html: true },
    ALLOWED_TAGS: baseTags,
    ALLOWED_ATTR: baseAttrs,
    ADD_ATTR: addAttrs,
  });
}
